import { useSortable } from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import clsx from "clsx"
import { memo, useMemo } from "react"

import useIsDesktop from "@hooks/useIsDesktop"

import { faIcon } from "@utils/faIcon"

import { Hint, Icon, IconButton, Toggle, Tooltip } from "@atoms"

import ListItem from "@molecules/ListItem/ListItem"
import ListItemContent from "@molecules/ListItem/ListItemContent/ListItemContent"

import useTableView from "@organisms/ObjectsView/TableView/TableView.context"

import styles from "./ColumnOrderingItem.module.scss"
import { ColumnOrderingItemProps } from "./ColumnOrderingItem.types"

function ColumnOrderingItem(props: ColumnOrderingItemProps) {
    const { columnId, isTabbable, isVisible, onPinColumn, onUnpinColumn, isPinned } = props

    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: columnId })

    const isDesktop = useIsDesktop()

    const { updateColumnVisibility, reachedPinLimit, reachedMinimumOfColumnsVisible, columns } = useTableView()

    const isVisibilityToggleDisabled = Boolean(isVisible && reachedMinimumOfColumnsVisible)
    const isPinButtonDisabled = reachedPinLimit && !isPinned

    const handleColumnVisibilityChange = () => {
        if (isVisibilityToggleDisabled) {
            return
        }
        updateColumnVisibility(columnId, !isVisible)
    }

    const handleColumnPinningChange = () => {
        if (isPinned) {
            onUnpinColumn(columnId)
        } else {
            // A pinned column has to be on screen, so pinning a hidden one reveals it too.
            if (!isVisible) {
                updateColumnVisibility(columnId, true)
            }
            onPinColumn(columnId)
        }
    }

    const label = useMemo(() => {
        const column = columns.find((col) => "accessorKey" in col && col.accessorKey === columnId)
        return column?.header as string
    }, [columnId, columns])

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        touchAction: "none",
    }

    const dragHandleIcon = useMemo(() => {
        if (isDragging) {
            return faIcon({ name: "arrows-up-down", style: "regular" })
        } else {
            return faIcon({ name: "grip-lines", style: "regular" })
        }
    }, [isDragging])

    return (
        <div
            ref={setNodeRef}
            style={style}
            {...attributes}
            {...listeners}
            tabIndex={-1}
            onClick={handleColumnVisibilityChange}
            className={clsx(styles.wrapper, { [styles.dragging]: isDragging })}
        >
            <Hint
                colorScheme="tomato"
                title="You can't hide this column"
                body="The table must have at least one column"
                defaultPlacement="top"
                isDisabled={!isVisibilityToggleDisabled}
            >
                <ListItem.Root className={styles.item}>
                    <ListItem.AvatarAndContent
                        avatar={
                            <div className={styles.dragHandle}>
                                <Icon icon={dragHandleIcon} size={14} />
                            </div>
                        }
                        content={<ListItemContent leftLabel={<ListItem.Label label={label} />} />}
                    />
                    <ListItem.Actions>
                        <div className={styles.actions} data-no-dnd="true" onClick={(e) => e.stopPropagation()}>
                            {isDesktop && (
                                <Hint
                                    colorScheme="tomato"
                                    title="You can only pin 3 columns"
                                    body="Only 3 columns can be pinned at a time. Please unpin one before pinning another."
                                    defaultPlacement="top"
                                    isDisabled={!isPinButtonDisabled}
                                >
                                    <Tooltip body={isPinned ? "Unpin" : "Pin"} isDisabled={isPinButtonDisabled}>
                                        <IconButton
                                            size="md"
                                            variant="ghost"
                                            isTabbable={isTabbable}
                                            isDisabled={isPinButtonDisabled}
                                            icon={faIcon({ name: "thumbtack", style: "regular" })}
                                            iconOnActive={faIcon({ name: "thumbtack", style: "solid" })}
                                            iconIsActive={isPinned}
                                            onClick={handleColumnPinningChange}
                                            noDebounce={true}
                                        />
                                    </Tooltip>
                                </Hint>
                            )}
                            <Toggle
                                id={`column-visibility-${columnId}`}
                                checked={isVisible}
                                onClick={handleColumnVisibilityChange}
                                isControlled={false}
                                isDisabled={isVisibilityToggleDisabled}
                            />
                        </div>
                    </ListItem.Actions>
                </ListItem.Root>
            </Hint>
        </div>
    )
}

export default memo(ColumnOrderingItem)
